/**
 * Composant DiplomesTab pour la page Satisfaction
 * Affiche le taux de réussite (diplômés) par année avec export PDF
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { GraduationCap, TrendingUp, Users, CheckCircle, XCircle, Download, Mail, Clock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export const DiplomesTab = () => {
  const [data, setData] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingRelance, setSendingRelance] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [tauxRes, alertesRes] = await Promise.all([
          axios.get('/blocs-competences/taux-reussite'),
          axios.get('/moniteurs/alertes-relance').catch(() => ({ data: [] }))
        ]);
        setData(tauxRes.data);
        setAlertes(alertesRes.data);
      } catch (e) {
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleRelance = async (userId, nom, echeance) => {
    const key = `${userId}-${echeance}`;
    setSendingRelance(key);
    try {
      await axios.post(`/moniteurs/relance/${userId}?echeance=${encodeURIComponent(echeance)}`);
      toast.success(`Email de relance (${echeance}) envoyé à ${nom}`);
      // Refresh alertes
      const res = await axios.get('/moniteurs/alertes-relance');
      setAlertes(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSendingRelance(null);
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Chargement...</div>;
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12" data-testid="diplomes-tab-empty">
        <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">Aucune donnée de session disponible</p>
      </div>
    );
  }

  const handleExportPDF = async () => {
    try {
      const jsPDFModule = await import('jspdf');
      const autoTableModule = await import('jspdf-autotable');
      const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
      const autoTable = autoTableModule.default;
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(18);
      doc.setTextColor(15, 23, 42);
      doc.text('Statistiques de Taux de Réussite', pageWidth / 2, 20, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`FISL - Centre de Formation Nautique | Généré le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, 28, { align: 'center' });

      let yPos = 38;

      data.forEach(d => {
        // Session header
        doc.setFontSize(13);
        doc.setTextColor(15, 23, 42);
        doc.text(`Session ${d.annee}`, 14, yPos);
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.text(`${d.diplomes}/${d.total_stagiaires} diplômés — Taux de réussite : ${d.taux_reussite}%`, 14, yPos + 6);
        yPos += 12;

        // Table
        const tableData = d.stagiaires.map(s => [
          `${s.prenom} ${s.nom}`,
          s.centre_formation || '-',
          `${s.bc_valides}/3`,
          s.diplome_obtenu ? 'Diplômé' : 'En cours'
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Stagiaire', 'Centre', 'BC validés', 'Statut']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [141, 198, 63], textColor: 255, fontSize: 9 },
          bodyStyles: { fontSize: 8 },
          columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' } },
          margin: { left: 14, right: 14 },
          didParseCell: (hookData) => {
            if (hookData.section === 'body' && hookData.column.index === 3) {
              hookData.cell.styles.textColor = hookData.cell.raw === 'Diplômé' ? [34, 197, 94] : [100, 116, 139];
              hookData.cell.styles.fontStyle = 'bold';
            }
          }
        });

        yPos = doc.lastAutoTable.finalY + 14;
        if (yPos > 260) { doc.addPage(); yPos = 20; }
      });

      doc.save(`${new Date().getFullYear()}_taux_reussite.pdf`);
      toast.success('PDF téléchargé avec succès');
    } catch (err) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  return (
    <div className="space-y-6" data-testid="diplomes-tab">
      {/* Header with export button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Taux de réussite par session</h2>
        <Button
          onClick={handleExportPDF}
          className="bg-[#8DC63F] hover:bg-[#7AB62F] text-white"
          data-testid="export-pdf-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Télécharger en PDF
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.map(d => (
          <Card key={d.annee} className="border-l-4 border-l-[#8DC63F]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Session {d.annee}</span>
                <Badge className={`text-white ${d.taux_reussite >= 80 ? 'bg-green-500' : d.taux_reussite >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}>
                  {d.taux_reussite}%
                </Badge>
              </div>
              <p className="text-3xl font-bold text-[#0F172A]">{d.diplomes}/{d.total_stagiaires}</p>
              <p className="text-xs text-slate-500 mt-1">stagiaires diplômés</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detail par année */}
      {data.map(d => (
        <div key={d.annee} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid={`diplomes-annee-${d.annee}`}>
          <div className="p-4 bg-slate-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-[#8DC63F]" />
              <h3 className="text-lg font-semibold text-[#0F172A]">Session {d.annee}</h3>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-slate-600">
                <Users className="h-4 w-4" /> {d.total_stagiaires} stagiaires
              </span>
              <span className="flex items-center gap-1 text-green-600 font-medium">
                <CheckCircle className="h-4 w-4" /> {d.diplomes} diplômés
              </span>
              <span className="flex items-center gap-1 text-slate-500">
                <TrendingUp className="h-4 w-4" /> {d.taux_reussite}% de réussite
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="px-4 pt-3">
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all ${d.taux_reussite >= 80 ? 'bg-green-500' : d.taux_reussite >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${d.taux_reussite}%` }}
              />
            </div>
          </div>

          {/* Table des stagiaires */}
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b text-left text-xs text-slate-500 uppercase">
                  <th className="p-2">Stagiaire</th>
                  <th className="p-2">Centre</th>
                  <th className="p-2 text-center">BC validés</th>
                  <th className="p-2 text-center">Statut</th>
                  <th className="p-2 text-center">Relance insertion</th>
                </tr>
              </thead>
              <tbody>
                {d.stagiaires.map(s => {
                  const moniteurAlertes = alertes.filter(a => a.user_id === s.stagiaire_id);
                  return (
                  <tr key={s.stagiaire_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-2 font-medium text-slate-800">{s.prenom} {s.nom}</td>
                    <td className="p-2 text-slate-600">{s.centre_formation || '-'}</td>
                    <td className="p-2 text-center">
                      <span className={`font-semibold ${s.bc_valides === 3 ? 'text-green-600' : 'text-slate-500'}`}>
                        {s.bc_valides}/3
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      {s.diplome_obtenu ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          <CheckCircle className="h-3 w-3 mr-1" /> Diplômé
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">
                          <XCircle className="h-3 w-3 mr-1" /> En cours
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      {s.diplome_obtenu && moniteurAlertes.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {moniteurAlertes.map(a => (
                            <Button
                              key={a.echeance}
                              size="sm"
                              variant={a.deja_relance ? "outline" : "default"}
                              className={`text-xs h-7 px-2 ${a.deja_relance ? 'border-green-300 text-green-600' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                              disabled={sendingRelance === `${a.user_id}-${a.echeance}`}
                              onClick={() => handleRelance(a.user_id, a.nom, a.echeance)}
                              data-testid={`relance-${a.user_id}-${a.echeance.replace(/\s/g, '-')}`}
                            >
                              {sendingRelance === `${a.user_id}-${a.echeance}` ? (
                                <Clock className="h-3 w-3 animate-spin" />
                              ) : a.deja_relance ? (
                                <><CheckCircle className="h-3 w-3 mr-1" />{a.echeance}</>
                              ) : (
                                <><Mail className="h-3 w-3 mr-1" />{a.echeance}</>
                              )}
                            </Button>
                          ))}
                        </div>
                      ) : s.diplome_obtenu ? (
                        <span className="text-xs text-slate-400">Pas encore éligible</span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DiplomesTab;
